import { Logs } from '../services/Logs.js';

const MODAL_ZINDEX_MIN = 100;
const MODAL_ZINDEX_MAX = 200;
const FLOATING_ZINDEX_MIN = 101;
const FLOATING_ZINDEX_MAX = 201;

export class UIModal {

    constructor(options = {}) {
        if (!options.id) {
            throw new Error("UIModal requires an id option");
        }

        this.options = {
            container: "#ui-container",
            close: true,
            remove_after_close: false,
            setDynamiquePosition: true,
            class: "",
            ...options
        };
        this.id = UIModal.normalizeRawId(this.options.id);
        this.options.id = this.id;
        this.selector = UIModal.normalizeId(this.id);
        this.build();

        return this;
    }

    build() {
        new Logs("message", ["CREATE MODAL", this.options]);

        const options = this.options;
        const zindex = UIModal.getMaxZindex();
        const container = document.querySelector(options.container);

        if (!container) {
            new Logs("error", [`Container ${options.container} non trouvé, modal impossible à créer`]);
            return false;
        }

        this.element?.remove();

        const modal = document.createElement("div");
        modal.id = options.id;
        modal.classList.add("ui-modal");
        modal.classList.add(...UIModal.parseClasses(options.class));
        modal.append(UIModal.createDragHandle());

        if (options.close) {
            const close = document.createElement("div");
            close.classList.add("close");

            const closeIcon = document.createElement("img");
            closeIcon.src = "/images/close-grey.svg";
            close.append(closeIcon);
            modal.append(close);
        }

        const side = document.createElement("div");
        side.classList.add("side", "glass-icon");
        modal.append(side);

        modal.append(UIModal.createLoading());
        modal.append(this.createContent());
        container.append(modal);

        this.bindEvents();
        if (this.options.setDynamiquePosition) {
            this.position(zindex);
        } else if (this.element) {
            this.element.style.zIndex = zindex;
        }

        this.floatingElements.forEach((element) => {
            element.style.zIndex = Math.min(zindex + 1, FLOATING_ZINDEX_MAX);
        });

        return this;
    }

    createContent() {
        const content = document.createElement("div");
        content.classList.add("ui-modal-content");

        if (this.options.icon) {
            const icon = document.createElement("div");
            icon.classList.add("ui-modal-zoneicon", "glass-icon");

            const image = document.createElement("img");
            image.src = this.options.icon.startsWith("/") ? this.options.icon : `/images/${this.options.icon}`;
            icon.append(image);
            content.append(icon);
        }

        if (this.options.title || this.options.undertitle) {
            const zoneTitle = document.createElement("div");
            zoneTitle.classList.add("ui-modal-zonetitle");

            if (this.options.title) {
                const title = document.createElement("div");
                title.classList.add("ui-modal-zonetitle-title");
                title.textContent = this.options.title;
                zoneTitle.append(title);
            }

            if (this.options.undertitle) {
                const undertitle = document.createElement("div");
                undertitle.classList.add("undertitle");
                undertitle.textContent = this.options.undertitle;
                zoneTitle.append(undertitle);
            }

            content.append(zoneTitle);
        }

        if (this.options.content) {
            content.insertAdjacentHTML("beforeend", this.options.content);
        }

        return content;
    }

    bindEvents() {
        if (this.options.close) {
            this.element?.querySelector(".close")?.addEventListener("click", () => {
                if (this.options.remove_after_close) {
                    this.remove();
                } else {
                    this.hide();
                }

                this.floatingElements.forEach((element) => element.remove());
            });
        }

        this.bindDragEvents();
        this.element?.querySelector(".side")?.addEventListener("click", () => this.turn());
        this.bindSideSwipeEvents();
        this.element?.addEventListener("click", (event) => {
            event.stopPropagation();
            this.setFront();
        });

        if (this.options.side) {
            this.element?.querySelector(".side")?.classList.add("visible");
        }
    }

    position(zindex) {
        const element = this.element;
        if (!element) return false;

        const modalIndex = UIModal.count() - 1;
        element.style.zIndex = zindex;

        if (document.body.classList.contains("mobile")) {
            // Mobile positioning is handled in CSS.
            // element.style.top = `${parseInt(modalIndex * 20 + 51)}px`;
            // element.style.maxHeight = `calc(100% - ${parseInt(modalIndex * 20 + 130)}px)`;
        } else {
            const top = parseInt(modalIndex * 20 + 51);
            element.style.top = `${top}px`;
            element.style.left = `${parseInt(modalIndex * 20 + 10)}px`;
            element.style.maxHeight = `calc(100% - ${parseInt(modalIndex * 20 + 70)}px)`;
        }

        return this;
    }

    bindDragEvents() {
        new Logs("message", ["UI MODAL BIND DRAG EVENTS", this.selector]);

        this.element?.querySelector(".ui-modal-drag-handle")?.addEventListener("pointerdown", (event) => {
            this.startDrag(event);
        });

        return this;
    }

    bindSideSwipeEvents() {
        new Logs("message", ["UI MODAL BIND SIDE SWIPE EVENTS", this.selector]);

        const element = this.element;
        if (!element || !this.options.side || !this.isMobileSideMode()) return false;

        element.addEventListener("touchstart", (event) => this.startSideSwipe(event), {passive: true});
        element.addEventListener("touchend", (event) => this.endSideSwipe(event), {passive: true});

        return this;
    }

    isMobileSideMode() {
        new Logs("message", ["UI MODAL IS MOBILE SIDE MODE", this.selector]);

        return document.body.classList.contains("mobile") || document.body.classList.contains("native");
    }

    startSideSwipe(event) {
        new Logs("message", ["UI MODAL START SIDE SWIPE", this.selector]);

        const touch = event.changedTouches?.[0];
        if (!touch) return false;

        this.sideSwipeState = {
            startX: touch.pageX,
            startY: touch.pageY,
            startTime: Date.now()
        };

        return this;
    }

    endSideSwipe(event) {
        new Logs("message", ["UI MODAL END SIDE SWIPE", this.selector]);

        if (!this.sideSwipeState) return false;

        const touch = event.changedTouches?.[0];
        if (!touch) return false;

        const distX = touch.pageX - this.sideSwipeState.startX;
        const distY = touch.pageY - this.sideSwipeState.startY;
        const elapsedTime = Date.now() - this.sideSwipeState.startTime;
        this.sideSwipeState = null;

        if (elapsedTime > 600 || Math.abs(distX) < 90 || Math.abs(distY) > 80) return false;

        if (distX < 0 && !this.element?.classList.contains("turn")) {
            return this.turn();
        }

        if (distX > 0 && this.element?.classList.contains("turn")) {
            return this.turn();
        }

        return this;
    }

    startDrag(event) {
        new Logs("message", ["UI MODAL START DRAG", this.selector]);

        const element = this.element;
        if (!element || !this.canDrag(event)) return false;

        const parent = element.offsetParent || element.parentElement;
        const parentRect = parent?.getBoundingClientRect() || {top: 0, left: 0, width: window.innerWidth, height: window.innerHeight};
        const rect = element.getBoundingClientRect();

        this.setFront();
        this.dragState = {
            parentRect,
            pointerId: event.pointerId,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top
        };

        element.classList.add("dragging");
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.preventDefault();

        this.dragMoveHandler = (moveEvent) => this.dragMove(moveEvent);
        this.dragEndHandler = (endEvent) => this.endDrag(endEvent);
        document.addEventListener("pointermove", this.dragMoveHandler);
        document.addEventListener("pointerup", this.dragEndHandler);
        document.addEventListener("pointercancel", this.dragEndHandler);

        return this;
    }

    canDrag(event) {
        new Logs("message", ["UI MODAL CAN DRAG", this.selector]);

        if (document.body.classList.contains("mobile")) return false;
        if (event.button !== 0) return false;
        if (this.element?.classList.contains("ui-modal-fullscreen")) return false;
        if (this.element?.classList.contains("turn")) return false;

        return true;
    }

    dragMove(event) {
        new Logs("message", ["UI MODAL DRAG MOVE", this.selector]);

        const element = this.element;
        if (!element || !this.dragState || event.pointerId !== this.dragState.pointerId) return false;

        const parentRect = this.dragState.parentRect;
        const rect = element.getBoundingClientRect();
        const left = event.clientX - parentRect.left - this.dragState.offsetX;
        const top = event.clientY - parentRect.top - this.dragState.offsetY;
        const maxLeft = Math.max(parentRect.width - rect.width, 0);
        const maxTop = Math.max(parentRect.height - rect.height, 0);
        const nextLeft = Math.min(Math.max(left, 0), maxLeft);
        const nextTop = Math.min(Math.max(top, 0), maxTop);

        element.style.left = `${nextLeft}px`;
        element.style.top = `${nextTop}px`;
        element.style.maxHeight = `calc(100% - ${nextTop}px)`;

        return this;
    }

    endDrag(event) {
        new Logs("message", ["UI MODAL END DRAG", this.selector]);

        if (this.dragState && event.pointerId !== this.dragState.pointerId) return false;

        this.element?.classList.remove("dragging");
        document.removeEventListener("pointermove", this.dragMoveHandler);
        document.removeEventListener("pointerup", this.dragEndHandler);
        document.removeEventListener("pointercancel", this.dragEndHandler);
        this.dragMoveHandler = null;
        this.dragEndHandler = null;
        this.dragState = null;
        this.updateFloatingPositions();

        return this;
    }

    updateFloatingPositions() {
        new Logs("message", ["UI MODAL UPDATE FLOATING POSITIONS", this.selector]);

        const element = this.element;
        if (!element) return false;

        const modalRect = element.getBoundingClientRect();
        const containerRect = document.getElementById("ui-container")?.getBoundingClientRect() || {top: 0, left: 0};
        const modalZindex = parseInt(element.style.zIndex || MODAL_ZINDEX_MIN);

        this.floatingElements.forEach((floatingElement) => {
            const floatingRect = floatingElement.getBoundingClientRect();
            const left = modalRect.left + modalRect.width - floatingRect.width - 70 - containerRect.left;
            let top = modalRect.top + modalRect.height - floatingRect.height - 20 - containerRect.top;
            if (top < 50) top += 80;

            floatingElement.style.left = `${left}px`;
            floatingElement.style.top = `${top}px`;
            floatingElement.style.zIndex = Math.min(modalZindex + 1, FLOATING_ZINDEX_MAX);
        });

        return this;
    }

    show() {
        new Logs("message", ["SHOW MODAL", this.selector]);

        setTimeout(() => {
            this.element?.classList.add("visible");
        }, 100);

        const list = document.getElementById("list");
        if (list) list.style.display = "none";

        return this;
    }

    hide() {
        new Logs("message", ["HIDE MODAL", this.selector]);

        this.element?.classList.remove("visible");

        return this;
    }

    remove() {
        new Logs("message", ["REMOVE MODAL", this.selector]);

        this.hide();

        setTimeout(() => {
            this.element?.remove();
        }, 500);

        return this;
    }

    setFront() {
        new Logs("message", ["UI MODAL SET FRONT", this.selector]);

        if (UIModal.count() <= 1) {
            new Logs("warning", ["Just one modal, no need to set front"]);
            return false;
        }

        document.querySelectorAll(".ui-modal").forEach((modal) => {
            modal.style.zIndex = MODAL_ZINDEX_MIN;
        });
        document.querySelectorAll(".floating-element").forEach((element) => {
            element.style.zIndex = FLOATING_ZINDEX_MIN;
        });

        const zindex = UIModal.getMaxZindex();
        if (this.element) this.element.style.zIndex = Math.min(zindex + 1, MODAL_ZINDEX_MAX);
        this.floatingElements.forEach((element) => {
            element.style.zIndex = Math.min(zindex + 2, FLOATING_ZINDEX_MAX);
        });

        return this;
    }

    notificationShow() {
        new Logs("message", ["UI MODAL NOTIFICATION SHOW", this.selector]);

        this.element?.querySelectorAll(".ui-modal-notification").forEach((notification) => {
            notification.classList.add("visible");
        });
        this.element?.classList.add("unblured");

        return this;
    }

    notificationHide() {
        new Logs("message", ["UI MODAL NOTIFICATION HIDE", this.selector]);

        this.element?.querySelectorAll(".ui-modal-notification").forEach((notification) => {
            notification.classList.remove("visible");
        });
        this.element?.classList.remove("unblured");

        return this;
    }

    loadingShow() {
        new Logs("message", ["UI MODAL LOADING SHOW", this.selector]);

        this.element?.querySelector(".ui-modal-loading")?.classList.add("visible");
        this.element?.classList.add("unblured");

        return this;
    }

    loadingHide() {
        new Logs("message", ["UI MODAL LOADING HIDE", this.selector]);

        this.element?.querySelector(".ui-modal-loading")?.classList.remove("visible");
        this.element?.classList.remove("unblured");

        return this;
    }

    setProgress(perCent, textAfter100 = null) {
        new Logs("message", ["SET MODAL PROGRESS", this.selector, perCent]);

        const progress = this.element?.querySelector(".ui-modal-loading .progress");
        const value = this.element?.querySelector(".ui-modal-loading .progress .value");
        const text = this.element?.querySelector(".ui-modal-loading .text");

        progress?.classList.add("visible");
        if (value) value.textContent = `${parseInt(perCent).toFixed(0)}%`;

        if (textAfter100 && perCent >= 100) {
            if (text) text.style.display = "none";
            if (value) value.textContent = textAfter100;
        }

        return this;
    }

    turn() {
        new Logs("message", ["UI MODAL TURN", this.selector]);

        const element = this.element;
        if (!element) return false;

        let isTurned = false;
        if (element.classList.contains("turn")) {
            let countModal = UIModal.count();
            if (countModal === 1) countModal = 0;
            const left = 10 + (countModal * 10);

            element.classList.remove("turn");
            element.style.left = `${left}px`;
        } else {
            const left = -(element.offsetWidth - 31);

            element.classList.add("turn");
            element.style.left = `${left}px`;
            isTurned = true;
        }

        element.dispatchEvent(new CustomEvent("ui-modal:turn", {
            bubbles: true,
            detail: {
                id: this.id,
                isTurned
            }
        }));

        return this;
    }

    get element() {
        return document.getElementById(this.id);
    }

    get floatingElements() {
        return document.querySelectorAll(`.floating-for-modal-${UIModal.escapeCss(this.id)}`);
    }

    static createLoading() {
        const loading = document.createElement("div");
        loading.classList.add("ui-modal-loading");

        const hankBall = document.createElement("div");
        hankBall.classList.add("hankBall");

        const text = document.createElement("div");
        text.classList.add("text");
        text.textContent = "Chargement en cours";

        const progress = document.createElement("div");
        progress.classList.add("progress");

        const value = document.createElement("div");
        value.classList.add("value");

        progress.append(value);
        loading.append(hankBall, text, progress);

        return loading;
    }

    static createDragHandle() {
        new Logs("message", ["UI MODAL CREATE DRAG HANDLE"]);

        const handle = document.createElement("div");
        handle.classList.add("ui-modal-drag-handle");
        handle.setAttribute("aria-hidden", "true");

        return handle;
    }

    static from(id) {
        const selector = UIModal.normalizeId(id);
        const modal = Object.create(UIModal.prototype);
        modal.id = UIModal.normalizeRawId(selector);
        modal.selector = selector;
        modal.options = {
            id: modal.id
        };
        return modal;
    }

    static normalizeRawId(id) {
        return id.substring(0, 1) === "#" ? id.substring(1) : id;
    }

    static normalizeId(id) {
        return id.substring(0, 1) === "#" ? id : `#${id}`;
    }

    static parseClasses(classes) {
        if (!classes) return [];
        return String(classes).split(" ").filter(Boolean);
    }

    static escapeCss(value) {
        if (globalThis.CSS && typeof globalThis.CSS.escape === "function") {
            return globalThis.CSS.escape(value);
        }

        return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
    }

    static count() {
        const modals = document.querySelectorAll(".ui-modal");
        new Logs("message", [`Found ${modals.length} modals`]);
        return modals.length;
    }

    static getMaxZindex() {
        new Logs("message", ["UI MODAL GET MAX Z-INDEX"]);

        const modals = document.querySelectorAll(".ui-modal");
        let zindex = MODAL_ZINDEX_MIN;
        let maxIndex = 0;

        modals.forEach((modal) => {
            const z = parseInt(modal.style.zIndex);
            if (z > maxIndex) maxIndex = z;
        });

        if (!modals || modals.length === 0) return zindex;

        zindex += modals.length + 2;
        if (maxIndex > zindex) zindex = maxIndex + 2;
        if (zindex > MODAL_ZINDEX_MAX) zindex = MODAL_ZINDEX_MAX;

        return zindex;
    }

    static createId() {
        return `ui-modal-${Math.random().toString(36).slice(2, 10)}`;
    }

}

globalThis.UIModal = UIModal;
